/**
 * ============================================================
 * UNIFIED TEST COMPONENT - VidyaPariksha
 * ============================================================
 * 
 * Production-grade online examination system combining:
 * - MCQ Tests (with multiple sections)
 * - Coding/DSA Tests
 * - Code Snippet MCQ Tests
 * 
 * Features:
 * 1. Global Exam Window + Dynamic Timer Logic
 * 2. MCQ with Multiple Sections & Section Timers
 * 3. Safe Timer + Auto-Submit Hard Lock
 * 4. Question-ID Based Answer Storage
 * 5. Exam Security Monitoring (SEB-like)
 * 
 * ============================================================
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { ref, onValue, push, get, set, update } from "firebase/database";
import { db } from "../services/firebase";
import { useNavigate, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import axios from "axios";
import ExamSecurityMonitor from "./ExamSecurityMonitor";
import CodeSnippetResult from "./CodeSnippetResult";
import { 
  FaClock, FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
  FaChevronLeft, FaChevronRight, FaBookOpen, FaCode, FaLaptopCode,
  FaArrowRight, FaRocket, FaDesktop, FaMemory, FaMicrochip, FaWifi
} from "react-icons/fa";
import "./VidyaPariksha.css";

const VidyaPariksha = ({ user }) => {
  const { quizId: quizIdFromParams } = useParams();
  const navigate = useNavigate();

  // ========== CORE STATE ==========
  const [quizType, setQuizType] = useState(null); // "mcq" | "coding" | "codeSnippet"
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ========== COMBINED TEST STATE ==========
  const [availableTestTypes, setAvailableTestTypes] = useState([]); // ["mcq", "codeSnippet", "coding"]
  const [completedTestTypes, setCompletedTestTypes] = useState([]); // Track completed parts
  const [combinedTestResults, setCombinedTestResults] = useState({}); // Store results for each part
  
  // Get quizId from URL params OR from localStorage (for student flow)
  const getQuizId = () => {
    if (quizIdFromParams) return quizIdFromParams;
    
    // Try to get from localStorage (student flow)
    const activeTest = JSON.parse(localStorage.getItem("activeTest") || "{}");
    if (activeTest.testId) {
      return activeTest.testId;
    }
    
    // Try from studentSession
    const studentSession = JSON.parse(localStorage.getItem("studentSession") || "{}");
    if (studentSession.testId) {
      return studentSession.testId;
    }
    
    return null;
  };
  
  const quizId = getQuizId();

  // ========== EXAM WINDOW & TIMER STATE ==========
  const [examStartTime, setExamStartTime] = useState(null);
  const [examEndTime, setExamEndTime] = useState(null);
  const [testDuration, setTestDuration] = useState(null); // in seconds
  const [effectiveTime, setEffectiveTime] = useState(null); // calculated available time
  const [timeRemaining, setTimeRemaining] = useState(null); // countdown
  const [testStartTime, setTestStartTime] = useState(null); // when user actually started
  const [isActive, setIsActive] = useState(false);
  const [startCountdown, setStartCountdown] = useState(null); // pre-test countdown

  // ========== TIMER & SUBMIT LOCKS ==========
  const timerIntervalRef = useRef(null);
  const submitLockRef = useRef(false); // Prevents duplicate submissions
  const answersRef = useRef({}); // Sync answers for auto-submit

  // ========== MCQ STATE (WITH SECTIONS) ==========
  const [sections, setSections] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sectionTimers, setSectionTimers] = useState({}); // { sectionId: remainingSeconds }
  const [sectionAnswers, setSectionAnswers] = useState({}); // { sectionId: { questionId: answer } }
  const [sectionStartTime, setSectionStartTime] = useState(null);

  // ========== CODE SNIPPET STATE ==========
  const [codeSnippetQuestions, setCodeSnippetQuestions] = useState([]);
  const [currentCodeSnippetIndex, setCurrentCodeSnippetIndex] = useState(0);
  const [codeSnippetAnswers, setCodeSnippetAnswers] = useState({}); // { questionId: answer }

  // ========== CODING STATE ==========
  const [codingQuestions, setCodingQuestions] = useState([]);
  const [currentCodingIndex, setCurrentCodingIndex] = useState(0);
  const [codeAnswers, setCodeAnswers] = useState({}); // { questionId: code }
  const [testResults, setTestResults] = useState({}); // { questionId: testCaseResults }
  const [language, setLanguage] = useState("71"); // Python default
  const [theme, setTheme] = useState(() => localStorage.getItem("editor-theme") || "vs-dark");

  // ========== UI STATE ==========
  const [submitting, setSubmitting] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // ========== JUDGE0 API CONFIG ==========
  const JUDGE0_API = "https://judge0-ce.p.rapidapi.com/submissions?base64_encoded=false&wait=true";
  const RAPID_API_KEY = "ce03d24195mshe8bf56141b4b145p19c2cbjsn01fc831b42c9";

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return "00:00:00";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // ============================================================
  // LOAD QUIZ DATA
  // ============================================================

  useEffect(() => {
    const loadQuiz = async () => {
      try {
        setLoading(true);
        
        // Get quizId - use current value or fetch from localStorage
        const currentQuizId = quizIdFromParams || 
          JSON.parse(localStorage.getItem("activeTest") || "{}").testId ||
          JSON.parse(localStorage.getItem("studentSession") || "{}").testId;
        const testIdToLoad = currentQuizId || "DEFAULT";
        
        // If DEFAULT, try defaultTests first
        if (testIdToLoad === "DEFAULT" || testIdToLoad === "MAIN-DEFAULT-001") {
          const defaultRef = ref(db, `defaultTests/MAIN-DEFAULT-001`);
          const defaultSnap = await get(defaultRef);
          
          if (defaultSnap.exists()) {
            const data = defaultSnap.val();
            // Handle default test structure (may have different format)
            // For now, try to extract sections or questions
            if (data.sections) {
              setQuizType("mcq");
              setQuizData(data);
              const sectionsArray = Object.keys(data.sections).map(key => ({
                id: key,
                ...data.sections[key]
              }));
              setSections(sectionsArray);
              const timers = {};
              sectionsArray.forEach(section => {
                timers[section.id] = (section.time || section.sectionDuration || 30) * 60;
              });
              setSectionTimers(timers);
              setLoading(false);
              return;
            }
          }
        }

        // Check all three paths for the same quizId (combined test support)
        const [mcqSnap, codeSnippetSnap, codingSnap] = await Promise.all([
          get(ref(db, `quizzes/${testIdToLoad}`)),
          get(ref(db, `codeSnippetQuestions/${testIdToLoad}`)),
          get(ref(db, `codingQuestions/${testIdToLoad}`))
        ]);

        const hasMCQ = mcqSnap.exists();
        const hasCodeSnippet = codeSnippetSnap.exists();
        const hasCoding = codingSnap.exists();

        // Determine if this is a combined test (multiple types)
        const testTypes = [];
        if (hasMCQ) testTypes.push("mcq");
        if (hasCodeSnippet) testTypes.push("codeSnippet");
        if (hasCoding) testTypes.push("coding");

        if (testTypes.length === 0) {
          // No test found - will show error below
          setError(`Quiz not found: ${testIdToLoad}`);
          setLoading(false);
          return;
        } else if (testTypes.length === 1) {
          // Single type test - load as before
          const singleType = testTypes[0];
          
          if (singleType === "mcq") {
            const data = mcqSnap.val();
            setQuizType("mcq");
            setQuizData(data);
            
            if (data.examStartTime) setExamStartTime(new Date(data.examStartTime));
            if (data.examEndTime) setExamEndTime(new Date(data.examEndTime));
            if (data.testDuration) setTestDuration(data.testDuration * 60);
            
            if (data.sections) {
              const sectionsArray = Object.keys(data.sections).map(key => ({
                id: key,
                ...data.sections[key]
              }));
              setSections(sectionsArray);
              
              const timers = {};
              sectionsArray.forEach(section => {
                timers[section.id] = section.sectionDuration * 60;
              });
              setSectionTimers(timers);
            } else {
              setSections([{
                id: "section1",
                sectionTitle: "Main Section",
                sectionDuration: data.countdown || 30,
                questions: Object.values(data.questions || {})
              }]);
            }
          } else if (singleType === "codeSnippet") {
            const data = codeSnippetSnap.val();
            setQuizType("codeSnippet");
            setQuizData(data);
            
            if (data.examStartTime) setExamStartTime(new Date(data.examStartTime));
            if (data.examEndTime) setExamEndTime(new Date(data.examEndTime));
            if (data.testDuration) setTestDuration(data.testDuration * 60);
            
            const questions = Object.values(data.questions || {});
            setCodeSnippetQuestions(questions);
            setLoading(false);
            return;
          } else if (singleType === "coding") {
            const data = codingSnap.val();
            setQuizType("coding");
            setQuizData(data);
            
            if (data.examStartTime) setExamStartTime(new Date(data.examStartTime));
            if (data.examEndTime) setExamEndTime(new Date(data.examEndTime));
            if (data.testDuration) setTestDuration(data.testDuration * 60);
            
            const questions = Object.values(data.questions || {});
            setCodingQuestions(questions);
            setLoading(false);
            return;
          }
          setLoading(false);
          return;
        } else {
          // Combined test - load all types
          // Set available test types in order: MCQ -> Code Snippet -> Coding
          setAvailableTestTypes(testTypes);
          
          // Load MCQ if exists
          if (hasMCQ) {
            const data = mcqSnap.val();
            setQuizType("mcq"); // Start with MCQ
            setQuizData(data);
            
            if (data.examStartTime) setExamStartTime(new Date(data.examStartTime));
            if (data.examEndTime) setExamEndTime(new Date(data.examEndTime));
            if (data.testDuration) setTestDuration(data.testDuration * 60);
            
            if (data.sections) {
              const sectionsArray = Object.keys(data.sections).map(key => ({
                id: key,
                ...data.sections[key]
              }));
              setSections(sectionsArray);
              
              const timers = {};
              sectionsArray.forEach(section => {
                timers[section.id] = section.sectionDuration * 60;
              });
              setSectionTimers(timers);
            }
          }
          
          // Load Code Snippet if exists
          if (hasCodeSnippet) {
            const data = codeSnippetSnap.val();
            const questions = Object.values(data.questions || {});
            setCodeSnippetQuestions(questions);
            
            // Use exam window from first available test
            if (!examStartTime && data.examStartTime) setExamStartTime(new Date(data.examStartTime));
            if (!examEndTime && data.examEndTime) setExamEndTime(new Date(data.examEndTime));
            if (!testDuration && data.testDuration) setTestDuration(data.testDuration * 60);
          }
          
          // Load Coding if exists
          if (hasCoding) {
            const data = codingSnap.val();
            const questions = Object.values(data.questions || {});
            setCodingQuestions(questions);
            
            // Use exam window from first available test
            if (!examStartTime && data.examStartTime) setExamStartTime(new Date(data.examStartTime));
            if (!examEndTime && data.examEndTime) setExamEndTime(new Date(data.examEndTime));
            if (!testDuration && data.testDuration) setTestDuration(data.testDuration * 60);
          }
          
          setLoading(false);
          return;
        }

        // Fallback: If still not found and it's DEFAULT, show error
        if (testIdToLoad === "DEFAULT") {
          setError("Default test not configured. Please contact administrator.");
        } else {
          setError(`Quiz not found: ${testIdToLoad}`);
        }
      } catch (err) {
        console.error("Error loading quiz:", err);
        setError("Failed to load quiz: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadQuiz();
  }, [quizId]);

  // ============================================================
  // SUBMIT HANDLER (DEFINED EARLY FOR useEffects)
  // ============================================================

  const handleSubmit = useCallback(async (isAutoSubmit = false) => {
    // HARD LOCK: Prevent duplicate submissions
    if (submitLockRef.current) {
      console.log("Submit already in progress, ignoring duplicate call");
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);

    // Stop all timers immediately
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    try {
      // Check if this is a student flow (from studentSession/activeTest)
      const activeTest = JSON.parse(localStorage.getItem("activeTest") || "{}");
      const studentSession = JSON.parse(localStorage.getItem("studentSession") || "{}");
      const isStudentFlow = !!(activeTest.specialStudentId || studentSession.studentId);
      const specialStudentId = activeTest.specialStudentId || studentSession.specialStudentId;

      // Use ref for auto-submit, current state for manual submit
      const finalAnswers = isAutoSubmit ? answersRef.current : 
        (quizType === "mcq" ? sectionAnswers : 
         quizType === "codeSnippet" ? codeSnippetAnswers : codeAnswers);

      // Check if this is a combined test (has multiple types loaded)
      const hasMultipleTypes = availableTestTypes.length > 1 || 
        (sections.length > 0 && codeSnippetQuestions.length > 0) ||
        (sections.length > 0 && codingQuestions.length > 0) ||
        (codeSnippetQuestions.length > 0 && codingQuestions.length > 0) ||
        (sections.length > 0 && codeSnippetQuestions.length > 0 && codingQuestions.length > 0);
      const isCombinedTest = hasMultipleTypes;
      
      // Inline submit logic to avoid dependency issues
      if (quizType === "mcq") {
        let totalScore = 0;
        let totalMarks = 0;
        const answerDetails = [];
        sections.forEach((section) => {
          const sectionAnswers = finalAnswers[section.id] || {};
          section.questions.forEach((question, qi) => {
            const questionId = question.id || `${section.id}-q${qi}`;
            const userAnswer = sectionAnswers[questionId];
            const isCorrect = userAnswer === question.correctAnswer;
            const marks = parseInt(question.marks || 1);
            totalMarks += marks;
            if (isCorrect) totalScore += marks;
            answerDetails.push({
              sectionId: section.id,
              sectionTitle: section.sectionTitle,
              questionId,
              question: question.question,
              selected: userAnswer || null,
              correct: question.correctAnswer,
              isCorrect,
              marks,
              explanation: question.explanation || ""
            });
          });
        });
        const submission = {
          user: user?.email || "guest",
          quizId,
          testType: "mcq",
          topic: quizData?.topic || "General",
          quizTitle: quizData?.title || "MCQ Test",
          score: totalScore,
          totalMarks,
          accuracy: ((totalScore / totalMarks) * 100).toFixed(2),
          timeTaken: testStartTime ? `${Math.floor((Date.now() - testStartTime) / 1000)}s` : "N/A",
          submittedAt: new Date().toISOString(),
          isAutoSubmit,
          answers: answerDetails
        };
        
        // Store result for combined test
        if (isCombinedTest) {
          setCombinedTestResults(prev => ({ ...prev, mcq: submission }));
          setCompletedTestTypes(prev => [...prev, "mcq"]);
        }
        
        // Save to results (for regular users)
        await push(ref(db, "results"), submission);
        localStorage.setItem("latestMCQResult", JSON.stringify(submission));
        
        // If combined test, move to next part instead of navigating
        if (isCombinedTest) {
          // Find next test type
          const nextType = availableTestTypes.find(type => 
            type !== "mcq" && !completedTestTypes.includes(type)
          );
          
          if (nextType) {
            // Move to next part
            if (nextType === "codeSnippet") {
              setQuizType("codeSnippet");
              setCurrentCodeSnippetIndex(0);
              setCodeSnippetAnswers({});
              alert("✅ MCQ section completed! Moving to Code Snippet section...");
              submitLockRef.current = false; // Reset lock for next part
              setSubmitting(false);
              return;
            } else if (nextType === "coding") {
              setQuizType("coding");
              setCurrentCodingIndex(0);
              setCodeAnswers({});
              setTestResults({});
              alert("✅ MCQ section completed! Moving to Coding section...");
              submitLockRef.current = false; // Reset lock for next part
              setSubmitting(false);
              return;
            }
          }
        }
        
        // If student flow, also save to studentResults and update studentAttempts
        if (isStudentFlow && specialStudentId) {
          // For combined tests, merge all results
          if (isCombinedTest && availableTestTypes.length === completedTestTypes.length + 1) {
            // All parts completed, save combined result
            const combinedResult = {
              ...submission,
              combinedResults: { ...combinedTestResults, mcq: submission },
              testType: "combined",
              quizTitle: quizData?.title || "Combined Test"
            };
            await set(ref(db, `studentResults/${specialStudentId}`), {
              ...combinedResult,
              studentDetails: activeTest.details || studentSession.details,
              specialStudentId,
              loginId: studentSession.studentId,
              checked: false
            });
          } else if (!isCombinedTest) {
            // Single test, save normally
            await set(ref(db, `studentResults/${specialStudentId}`), {
              ...submission,
              studentDetails: activeTest.details || studentSession.details,
              specialStudentId,
              loginId: studentSession.studentId,
              checked: false
            });
          }
          
          // Update studentAttempts status only when all parts are done
          if (!isCombinedTest || availableTestTypes.length === completedTestTypes.length + 1) {
            await update(ref(db, `studentAttempts/${specialStudentId}`), {
              status: "submitted",
              completedAt: Date.now()
            });
          }
        }
        
        if (isAutoSubmit) alert("⏳ Time's up! Your answers have been auto-submitted.");
        
        // Navigate based on flow (only if not combined test or all parts done)
        if (isCombinedTest && availableTestTypes.length > completedTestTypes.length + 1) {
          // More parts remaining, don't navigate yet
          return;
        }
        
        if (isStudentFlow) {
          navigate("/result"); // Student result page
        } else if (quizData?.showResultAfterSubmit) {
          navigate("/mcq-result");
        } else {
          navigate("/dashboard");
        }
      } else if (quizType === "codeSnippet") {
        let totalScore = 0;
        let totalMarks = 0;
        const answerDetails = [];
        codeSnippetQuestions.forEach((question, index) => {
          const questionId = question.id || `q${index}`;
          const userAnswer = finalAnswers[questionId];
          const isCorrect = userAnswer === question.correctAnswer;
          const marks = parseInt(question.marks || 1);
          totalMarks += marks;
          if (isCorrect) totalScore += marks;
          answerDetails.push({
            questionId,
            question: question.title,
            selected: userAnswer || null,
            correct: question.correctAnswer,
            isCorrect,
            marks,
            explanation: question.explanation || ""
          });
        });
        const submission = {
          user: user?.email || "guest",
          quizId,
          testType: "codeSnippet",
          topic: quizData?.topic || "General",
          quizTitle: quizData?.title || "Code Snippet Test",
          score: totalScore,
          totalMarks,
          accuracy: ((totalScore / totalMarks) * 100).toFixed(2),
          timeTaken: testStartTime ? `${Math.floor((Date.now() - testStartTime) / 1000)}s` : "N/A",
          submittedAt: new Date().toISOString(),
          isAutoSubmit,
          answers: answerDetails
        };
        
        // Store result for combined test
        if (isCombinedTest) {
          setCombinedTestResults(prev => ({ ...prev, codeSnippet: submission }));
          setCompletedTestTypes(prev => [...prev, "codeSnippet"]);
        }
        
        // Save to results (for regular users)
        await push(ref(db, "results"), submission);
        localStorage.setItem("latestCodeSnippetResult", JSON.stringify(submission));
        
        // If combined test, move to next part instead of navigating
        if (isCombinedTest) {
          // Find next test type
          const nextType = availableTestTypes.find(type => 
            type !== "codeSnippet" && !completedTestTypes.includes(type)
          );
          
          if (nextType === "coding") {
            setQuizType("coding");
            setCurrentCodingIndex(0);
            setCodeAnswers({});
            setTestResults({});
            alert("✅ Code Snippet section completed! Moving to Coding section...");
            submitLockRef.current = false; // Reset lock for next part
            setSubmitting(false);
            return;
          }
        }
        
        // If student flow, also save to studentResults and update studentAttempts
        if (isStudentFlow && specialStudentId) {
          // For combined tests, merge all results
          if (isCombinedTest && availableTestTypes.length === completedTestTypes.length + 1) {
            // All parts completed, save combined result
            const combinedResult = {
              ...submission,
              combinedResults: { ...combinedTestResults, codeSnippet: submission },
              testType: "combined",
              quizTitle: quizData?.title || "Combined Test"
            };
            await set(ref(db, `studentResults/${specialStudentId}`), {
              ...combinedResult,
              studentDetails: activeTest.details || studentSession.details,
              specialStudentId,
              loginId: studentSession.studentId,
              checked: false
            });
            
            // Update studentAttempts status
            await update(ref(db, `studentAttempts/${specialStudentId}`), {
              status: "submitted",
              completedAt: Date.now()
            });
          } else if (!isCombinedTest) {
            // Single test, save normally
            await set(ref(db, `studentResults/${specialStudentId}`), {
              ...submission,
              studentDetails: activeTest.details || studentSession.details,
              specialStudentId,
              loginId: studentSession.studentId,
              checked: false
            });
            
            await update(ref(db, `studentAttempts/${specialStudentId}`), {
              status: "submitted",
              completedAt: Date.now()
            });
          }
        }
        
        if (isAutoSubmit) alert("⏳ Time's up! Your answers have been auto-submitted.");
        
        // Navigate based on flow (only if not combined test or all parts done)
        if (isCombinedTest && availableTestTypes.length > completedTestTypes.length + 1) {
          // More parts remaining, don't navigate yet
          return;
        }
        
        if (isStudentFlow) {
          navigate("/result"); // Student result page
        } else {
          setShowResults(true);
        }
      } else if (quizType === "coding") {
        let totalScore = 0;
        const answerDetails = [];
        for (let i = 0; i < codingQuestions.length; i++) {
          const question = codingQuestions[i];
          const questionId = question.id || `q${i}`;
          const code = finalAnswers[questionId] || "";
          const testCaseResults = testResults[questionId] || [];
          const passed = testCaseResults.filter(t => t.passed).length;
          const score = Math.round((passed / (question.testCases?.length || 1)) * question.marks);
          totalScore += score;
          answerDetails.push({
            questionId,
            question: question.title,
            code,
            testResults: testCaseResults,
            score,
            maxMarks: question.marks
          });
        }
        const submission = {
          user: user?.email || "guest",
          quizId,
          testType: "coding",
          topic: quizData?.topic || "General",
          quizTitle: quizData?.quizTitle || "Coding Test",
          score: totalScore,
          totalMarks: codingQuestions.reduce((sum, q) => sum + q.marks, 0),
          accuracy: codingQuestions.length > 0 ? ((totalScore / codingQuestions.reduce((sum, q) => sum + q.marks, 0)) * 100).toFixed(2) : "0.00",
          timeTaken: testStartTime ? `${Math.floor((Date.now() - testStartTime) / 1000)}s` : "N/A",
          submittedAt: new Date().toISOString(),
          isAutoSubmit,
          answers: answerDetails
        };
        
        // Store result for combined test
        if (isCombinedTest) {
          setCombinedTestResults(prev => ({ ...prev, coding: submission }));
          setCompletedTestTypes(prev => [...prev, "coding"]);
        }
        
        // Save to results (for regular users)
        await push(ref(db, "results"), submission);
        localStorage.setItem("latestCodingResult", JSON.stringify(submission));
        
        // If student flow, also save to studentResults and update studentAttempts
        if (isStudentFlow && specialStudentId) {
          // For combined tests, merge all results
          if (isCombinedTest) {
            // All parts completed, save combined result
            const totalCombinedScore = Object.values({ ...combinedTestResults, coding: submission })
              .reduce((sum, r) => sum + (r.score || 0), 0);
            const totalCombinedMarks = Object.values({ ...combinedTestResults, coding: submission })
              .reduce((sum, r) => sum + (r.totalMarks || 0), 0);
            
            const combinedResult = {
              ...submission,
              testType: "combined",
              quizTitle: quizData?.title || "Combined Test",
              score: totalCombinedScore,
              totalMarks: totalCombinedMarks,
              accuracy: totalCombinedMarks > 0 ? ((totalCombinedScore / totalCombinedMarks) * 100).toFixed(2) : "0.00",
              combinedResults: { ...combinedTestResults, coding: submission }
            };
            await set(ref(db, `studentResults/${specialStudentId}`), {
              ...combinedResult,
              studentDetails: activeTest.details || studentSession.details,
              specialStudentId,
              loginId: studentSession.studentId,
              checked: false
            });
            
            // Update studentAttempts status
            await update(ref(db, `studentAttempts/${specialStudentId}`), {
              status: "submitted",
              completedAt: Date.now()
            });
          } else {
            // Single test, save normally
            await set(ref(db, `studentResults/${specialStudentId}`), {
              ...submission,
              studentDetails: activeTest.details || studentSession.details,
              specialStudentId,
              loginId: studentSession.studentId,
              checked: false
            });
            
            await update(ref(db, `studentAttempts/${specialStudentId}`), {
              status: "submitted",
              completedAt: Date.now()
            });
          }
        }
        
        if (isAutoSubmit) alert("⏳ Time's up! Your code has been auto-submitted.");
        
        // Navigate based on flow (coding is always last, so navigate to result)
        if (isStudentFlow) {
          navigate("/result"); // Student result page
        } else if (quizData?.showResultAfterSubmit) {
          navigate("/coding-result");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert("❌ Error submitting test: " + error.message);
      submitLockRef.current = false; // Allow retry on error
    } finally {
      setSubmitting(false);
      // Keep lock true to prevent re-submission on success
    }
  }, [quizType, sectionAnswers, codeSnippetAnswers, codeAnswers, quizData, testStartTime, sections, codeSnippetQuestions, codingQuestions, testResults, user, quizId, navigate, availableTestTypes, completedTestTypes, combinedTestResults]);

  // ============================================================
  // GLOBAL EXAM WINDOW + DYNAMIC TIMER LOGIC
  // ============================================================

  useEffect(() => {
    if (!examStartTime || !examEndTime) return;

    const calculateTime = () => {
      const now = Date.now();
      const start = examStartTime.getTime();
      const end = examEndTime.getTime();

      // Test hasn't started yet
      if (now < start) {
        setIsActive(false);
        setStartCountdown(Math.floor((start - now) / 1000));
        setTimeRemaining(null);
        return;
      }

      // Test has ended
      if (now >= end) {
        setIsActive(false);
        setTimeRemaining(0);
        if (!submitLockRef.current) {
          // Use setTimeout to avoid calling handleSubmit during render
          setTimeout(() => {
            if (!submitLockRef.current) {
              handleSubmit(true);
            }
          }, 100);
        }
        return;
      }

      // Test is active - calculate effective time dynamically
      const remainingWindow = Math.floor((end - now) / 1000);
      
      // If testDuration is set, calculate based on when user started
      let effective = remainingWindow;
      if (testDuration && testStartTime) {
        const elapsed = Math.floor((now - testStartTime) / 1000);
        const remainingFromDuration = testDuration - elapsed;
        effective = Math.min(remainingFromDuration, remainingWindow);
      } else if (testDuration) {
        // First time - use testDuration or remainingWindow, whichever is smaller
        effective = Math.min(testDuration, remainingWindow);
      }

      // Ensure effective time is not negative
      effective = Math.max(0, effective);

      setEffectiveTime(effective);
      setTimeRemaining(effective);
      setIsActive(true);
      
      // Set test start time if not set (when user actually starts)
      if (!testStartTime) {
        setTestStartTime(now);
      }
    };

    // Calculate immediately
    calculateTime();

    // Set up interval to recalculate every second
    const interval = setInterval(calculateTime, 1000);

    return () => clearInterval(interval);
  }, [examStartTime, examEndTime, testDuration, testStartTime, handleSubmit]);

  // ============================================================
  // PRE-TEST COUNTDOWN TIMER
  // ============================================================

  useEffect(() => {
    if (startCountdown === null || startCountdown <= 0) return;

    const interval = setInterval(() => {
      setStartCountdown(prev => {
        if (prev <= 1) {
          setIsActive(true);
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [startCountdown]);

  // ============================================================
  // SECTION TIMER (FOR MCQ WITH SECTIONS)
  // ============================================================

  useEffect(() => {
    // Only run section timer for MCQ with sections
    if (quizType !== "mcq" || sections.length === 0 || currentSectionIndex >= sections.length) {
      return;
    }

    const currentSection = sections[currentSectionIndex];
    const sectionTimer = sectionTimers[currentSection?.id];
    
    if (sectionTimer === undefined || sectionTimer <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setSectionTimers(prev => {
        const sectionId = sections[currentSectionIndex]?.id;
        if (!sectionId) return prev;
        
        const newTime = (prev[sectionId] || 0) - 1;
        
        if (newTime <= 0) {
          // Auto-move to next section
          if (currentSectionIndex < sections.length - 1) {
            setCurrentSectionIndex(prev => prev + 1);
            // Reset section timer for next section
            const nextSection = sections[currentSectionIndex + 1];
            if (nextSection) {
              return {
                ...prev,
                [sectionId]: 0,
                [nextSection.id]: nextSection.sectionDuration * 60
              };
            }
          } else {
            // Last section ended - submit
            handleSubmit(true);
          }
          
          return { ...prev, [sectionId]: 0 };
        }
        
        return { ...prev, [sectionId]: newTime };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [quizType, sections, currentSectionIndex, sectionTimers, handleSubmit]);

  // Time remaining is now managed by the global exam window logic above
  // No need for separate initialization - it's calculated dynamically

  // ============================================================
  // SYNC ANSWERS TO REF (FOR AUTO-SUBMIT)
  // ============================================================

  useEffect(() => {
    if (quizType === "mcq") {
      answersRef.current = sectionAnswers;
    } else if (quizType === "codeSnippet") {
      answersRef.current = codeSnippetAnswers;
    } else if (quizType === "coding") {
      answersRef.current = codeAnswers;
    }
  }, [quizType, sectionAnswers, codeSnippetAnswers, codeAnswers]);

  // ============================================================
  // ANSWER HANDLERS (QUESTION-ID BASED)
  // ============================================================

  // MCQ Answer Handler
  const handleMCQAnswer = useCallback((sectionId, questionId, answer) => {
    setSectionAnswers(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [questionId]: answer
      }
    }));
  }, []);

  // Code Snippet Answer Handler
  const handleCodeSnippetAnswer = useCallback((questionId, answer) => {
    setCodeSnippetAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  }, []);

  // Coding Answer Handler
  const handleCodingAnswer = useCallback((questionId, code) => {
    setCodeAnswers(prev => ({
      ...prev,
      [questionId]: code
    }));
    localStorage.setItem(`draftCode-${questionId}`, code);
  }, []);



  // ============================================================
  // RENDER LOADING & ERROR STATES
  // ============================================================

  if (loading) {
    return (
      <div className="vidya-test-container">
        <div className="vidya-loading">⏳ Loading quiz...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vidya-test-container">
        <div className="vidya-error">❌ {error}</div>
        <button onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
      </div>
    );
  }

  if (!quizData || !quizType) {
    return (
      <div className="vidya-test-container">
        <div className="vidya-error">❌ Quiz data not available</div>
      </div>
    );
  }

  // ============================================================
  // PRE-TEST COUNTDOWN
  // ============================================================

  if (startCountdown !== null && startCountdown > 0) {
    return (
      <div className="vidya-test-container">
        <div className="vidya-pre-test">
          <h2>📘 {quizData.title || quizData.quizTitle}</h2>
          <div className="vidya-countdown">
            <FaClock /> Test starts in: <strong>{formatTime(startCountdown)}</strong>
          </div>
          <div className="vidya-instructions">
            <h3><FaBookOpen /> Instructions</h3>
            <ul>
              <li>✅ Read each question carefully</li>
              <li>⏱️ Manage your time wisely</li>
              <li>🚫 Do not switch tabs or use developer tools</li>
              <li>📝 Answers will be auto-submitted when time expires</li>
            </ul>
          </div>
        </div>
        <ExamSecurityMonitor onViolationLimitReached={() => handleSubmit(true)} />
      </div>
    );
  }

  // ============================================================
  // MAIN TEST RENDER (BY QUIZ TYPE)
  // ============================================================

  return (
    <div className="vidya-test-container">
      {/* Header with Timer */}
      <div className="vidya-header">
        <h2>
          {quizType === "mcq" && "📝"}
          {quizType === "codeSnippet" && "💻"}
          {quizType === "coding" && "🔧"}
          {" "}
          {quizData.title || quizData.quizTitle}
        </h2>
        
        <div className="vidya-timer">
          {quizType === "mcq" && sections.length > 0 ? (
            <>
              <div>Global: {formatTime(timeRemaining)}</div>
              {sections[currentSectionIndex] && (
                <div>
                  Section: {formatTime(sectionTimers[sections[currentSectionIndex].id] || 0)}
                </div>
              )}
            </>
          ) : (
            <div>⏱️ {formatTime(timeRemaining)}</div>
          )}
        </div>
      </div>

      {/* Test Content */}
      {quizType === "mcq" && renderMCQ()}
      {quizType === "codeSnippet" && renderCodeSnippet()}
      {quizType === "coding" && renderCoding()}

      {/* Security Monitor */}
      <ExamSecurityMonitor onViolationLimitReached={() => handleSubmit(true)} />

      {/* Submit Button */}
      {isActive && !submitting && (
        <div className="vidya-submit-section">
          <button 
            className="vidya-submit-btn" 
            onClick={() => handleSubmit(false)}
            disabled={submitting}
          >
            <FaRocket /> Submit Test
          </button>
        </div>
      )}
    </div>
  );

  // ============================================================
  // RENDER FUNCTIONS
  // ============================================================

  function renderMCQ() {
    if (sections.length === 0) return <div>No sections available</div>;
    
    const currentSection = sections[currentSectionIndex];
    if (!currentSection) return <div>Section not found</div>;

    return (
      <div className="vidya-mcq-container">
        <div className="vidya-section-header">
          <h3>{currentSection.sectionTitle}</h3>
          <p>Section {currentSectionIndex + 1} of {sections.length}</p>
        </div>

        <div className="vidya-questions">
          {currentSection.questions.map((question, qIndex) => {
            const questionId = question.id || `${currentSection.id}-q${qIndex}`;
            const selectedAnswer = sectionAnswers[currentSection.id]?.[questionId];

            return (
              <div key={questionId} className="vidya-question-card">
                <h4>Q{qIndex + 1}: {question.question}</h4>
                <ul className="vidya-options">
                  {question.options.map((opt, optIndex) => (
                    <li key={optIndex}>
                      <label
                        className={selectedAnswer === opt ? "selected" : ""}
                        onClick={() => handleMCQAnswer(currentSection.id, questionId, opt)}
                      >
                        <input
                          type="radio"
                          name={`q-${questionId}`}
                          checked={selectedAnswer === opt}
                          readOnly
                        />
                        {opt}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="vidya-section-nav">
          {currentSectionIndex > 0 && (
            <button onClick={() => setCurrentSectionIndex(prev => prev - 1)}>
              <FaChevronLeft /> Previous Section
            </button>
          )}
          {currentSectionIndex < sections.length - 1 && (
            <button onClick={() => setCurrentSectionIndex(prev => prev + 1)}>
              Next Section <FaChevronRight />
            </button>
          )}
        </div>
      </div>
    );
  }

  function renderCodeSnippet() {
    if (codeSnippetQuestions.length === 0) return <div>No questions available</div>;
    
    const currentQuestion = codeSnippetQuestions[currentCodeSnippetIndex];
    if (!currentQuestion) return <div>Question not found</div>;

    const questionId = currentQuestion.id || `q${currentCodeSnippetIndex}`;
    const selectedAnswer = codeSnippetAnswers[questionId];

    return (
      <div className="vidya-code-snippet-container">
        <div className="vidya-question-header">
          <h3>Q{currentCodeSnippetIndex + 1}: {currentQuestion.title}</h3>
          <p>{currentQuestion.description}</p>
          <div className="vidya-code-display">
            <pre>{currentQuestion.codeSnippet}</pre>
          </div>
        </div>

        <div className="vidya-options">
          {currentQuestion.options.map((opt, optIndex) => (
            <label
              key={optIndex}
              className={selectedAnswer === opt ? "selected" : ""}
              onClick={() => handleCodeSnippetAnswer(questionId, opt)}
            >
              <input
                type="radio"
                name={`cs-${questionId}`}
                checked={selectedAnswer === opt}
                readOnly
              />
              {opt}
            </label>
          ))}
        </div>

        <div className="vidya-nav">
          {currentCodeSnippetIndex > 0 && (
            <button onClick={() => setCurrentCodeSnippetIndex(prev => prev - 1)}>
              <FaChevronLeft /> Previous
            </button>
          )}
          {currentCodeSnippetIndex < codeSnippetQuestions.length - 1 && (
            <button onClick={() => setCurrentCodeSnippetIndex(prev => prev + 1)}>
              Next <FaChevronRight />
            </button>
          )}
        </div>
      </div>
    );
  }

  function renderCoding() {
    if (codingQuestions.length === 0) return <div>No questions available</div>;
    
    const currentQuestion = codingQuestions[currentCodingIndex];
    if (!currentQuestion) return <div>Question not found</div>;

    const questionId = currentQuestion.id || `q${currentCodingIndex}`;
    const code = codeAnswers[questionId] || "";

    return (
      <div className="vidya-coding-container">
        <div className="vidya-question-header">
          <h3>Q{currentCodingIndex + 1}: {currentQuestion.title}</h3>
          <p>{currentQuestion.description}</p>
          <p><strong>Marks:</strong> {currentQuestion.marks}</p>
        </div>

        <div className="vidya-editor-section">
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="71">Python 3</option>
            <option value="63">JavaScript</option>
            <option value="54">C++</option>
            <option value="62">Java</option>
            <option value="50">C</option>
          </select>

          <Editor
            height="400px"
            language={
              language === "71" ? "python" :
              language === "63" ? "javascript" :
              language === "54" ? "cpp" :
              language === "62" ? "java" : "c"
            }
            theme={theme}
            value={code}
            onChange={(val) => handleCodingAnswer(questionId, val)}
            options={{ fontSize: 14, minimap: { enabled: false } }}
          />
        </div>

        <div className="vidya-nav">
          {currentCodingIndex > 0 && (
            <button onClick={() => setCurrentCodingIndex(prev => prev - 1)}>
              <FaChevronLeft /> Previous
            </button>
          )}
          {currentCodingIndex < codingQuestions.length - 1 && (
            <button onClick={() => setCurrentCodingIndex(prev => prev + 1)}>
              Next <FaChevronRight />
            </button>
          )}
        </div>
      </div>
    );
  }
};

export default VidyaPariksha;
